import { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { Form, Input, TextArea, Button, Checkbox, Select } from 'semantic-ui-react';
import { errorInput, searchSelectProps } from 'src/components/helpers';

const FormContainer = styled.div`
  padding: 1rem 0;
`;

const FormSection = styled.div`
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h3`
  font-family: 'Raleway', sans-serif;
  color: #2c3e50;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #e9ecef;
`;

const FormField = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #2c3e50;
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  font-size: 0.875rem;
  margin-top: 0.25rem;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 2rem;
  padding-top: 1rem;
  border-top: 1px solid #e9ecef;
`;

const CourseForm = ({ course, onSave, onCancel }) => {
  const [inputs, setInputs] = useState({
    title: '',
    description: '',
    shortDescription: '',
    imageUrl: '',
    status: true,
    isPublic: true,
    difficulty: 'beginner',
    estimatedDuration: '',
    prerequisites: '',
    learningObjectives: '',
    tags: '',
  });
  const [errorInputs, setErrorInputs] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (course) {
      setInputs({
        title: course.title || '',
        description: course.description || '',
        shortDescription: course.shortDescription || '',
        imageUrl: course.imageUrl || '',
        status: course.status !== false,
        isPublic: course.isPublic !== false,
        difficulty: course.difficulty || 'beginner',
        estimatedDuration: course.estimatedDuration || '',
        prerequisites: course.prerequisites || '',
        learningObjectives: course.learningObjectives || '',
        tags: course.tags || '',
      });
    }
  }, [course]);

  const handleInputs = (event) => {
    const { name, value } = event.target;
    setInputs({ ...inputs, [name]: value });
    // Clear error when user starts typing
    if (errorInputs[name]) {
      setErrorInputs({ ...errorInputs, [name]: false });
    }
  };

  const handleSelect = (event, { name, value }) => {
    setInputs({ ...inputs, [name]: value });
    if (errorInputs[name]) {
      setErrorInputs({ ...errorInputs, [name]: false });
    }
  };

  const handleCheckbox = (name) => {
    setInputs({ ...inputs, [name]: !inputs[name] });
  };

  const verifyForm = () => {
    const mandatoryInputs = ['title', 'description', 'shortDescription'];
    let newErrorInputs = {};

    mandatoryInputs.forEach((input) => {
      newErrorInputs = errorInput({
        errorInputs: newErrorInputs,
        input: { name: input, value: inputs[input] },
      });
    });

    // Validate estimated duration format (e.g., "2 hours", "30 minutes")
    if (
      inputs.estimatedDuration &&
      !/^\d+\s*(hours?|minutes?|days?)$/i.test(inputs.estimatedDuration)
    ) {
      newErrorInputs.estimatedDuration = true;
    }

    setErrorInputs(newErrorInputs);
    return Object.values(newErrorInputs).includes(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (verifyForm()) return;

    setLoading(true);
    try {
      const courseData = {
        ...inputs,
        tags: inputs.tags
          .split(',')
          .map((tag) => tag.trim())
          .filter((tag) => tag),
        learningObjectives: inputs.learningObjectives.split('\n').filter((obj) => obj.trim()),
        prerequisites: inputs.prerequisites.split('\n').filter((prereq) => prereq.trim()),
      };

      await onSave(courseData);
    } catch (error) {
      console.error('Error saving course:', error);
    } finally {
      setLoading(false);
    }
  };

  const difficultyOptions = [
    { key: 'beginner', value: 'beginner', text: 'Principiante' },
    { key: 'intermediate', value: 'intermediate', text: 'Intermedio' },
    { key: 'advanced', value: 'advanced', text: 'Avanzado' },
  ];

  return (
    <FormContainer>
      <Form onSubmit={handleSubmit}>
        <FormSection>
          <SectionTitle>Información Básica</SectionTitle>

          <FormField>
            <Label>Título del Curso *</Label>
            <Input
              name="title"
              value={inputs.title}
              onChange={handleInputs}
              placeholder="Ingresa el título del curso"
              className={errorInputs.title ? 'error' : ''}
              fluid
            />
            {errorInputs.title && <ErrorMessage>El título es obligatorio</ErrorMessage>}
          </FormField>

          <FormField>
            <Label>Descripción Corta *</Label>
            <TextArea
              name="shortDescription"
              value={inputs.shortDescription}
              onChange={handleInputs}
              placeholder="Una descripción breve del curso (máximo 200 caracteres)"
              className={errorInputs.shortDescription ? 'error' : ''}
              rows={3}
              maxLength={200}
            />
            {errorInputs.shortDescription && (
              <ErrorMessage>La descripción corta es obligatoria</ErrorMessage>
            )}
          </FormField>

          <FormField>
            <Label>Descripción Completa *</Label>
            <TextArea
              name="description"
              value={inputs.description}
              onChange={handleInputs}
              placeholder="Descripción detallada del curso"
              className={errorInputs.description ? 'error' : ''}
              rows={6}
            />
            {errorInputs.description && <ErrorMessage>La descripción es obligatoria</ErrorMessage>}
          </FormField>

          <FormField>
            <Label>URL de la Imagen</Label>
            <Input
              name="imageUrl"
              value={inputs.imageUrl}
              onChange={handleInputs}
              placeholder="https://ejemplo.com/imagen.jpg"
              fluid
            />
          </FormField>
        </FormSection>

        <FormSection>
          <SectionTitle>Configuración del Curso</SectionTitle>

          <FormField>
            <Label>Nivel de Dificultad</Label>
            <Select
              placeholder="Selecciona el nivel de dificultad"
              fluid
              {...searchSelectProps({
                name: 'difficulty',
                value: inputs.difficulty,
                handler: handleSelect,
                options: difficultyOptions,
              })}
            />
          </FormField>

          <FormField>
            <Label>Duración Estimada</Label>
            <Input
              name="estimatedDuration"
              value={inputs.estimatedDuration}
              onChange={handleInputs}
              placeholder="Ej: 2 hours, 30 minutes, 1 day"
              className={errorInputs.estimatedDuration ? 'error' : ''}
              fluid
            />
            {errorInputs.estimatedDuration && (
              <ErrorMessage>Formato inválido. Usa: "2 hours", "30 minutes", "1 day"</ErrorMessage>
            )}
          </FormField>

          <FormField>
            <Label>Etiquetas</Label>
            <Input
              name="tags"
              value={inputs.tags}
              onChange={handleInputs}
              placeholder="etiqueta1, etiqueta2, etiqueta3"
              fluid
            />
          </FormField>
        </FormSection>

        <FormSection>
          <SectionTitle>Contenido del Curso</SectionTitle>

          <FormField>
            <Label>Prerrequisitos</Label>
            <TextArea
              name="prerequisites"
              value={inputs.prerequisites}
              onChange={handleInputs}
              placeholder="Lista de prerrequisitos (uno por línea)"
              rows={4}
            />
          </FormField>

          <FormField>
            <Label>Objetivos de Aprendizaje</Label>
            <TextArea
              name="learningObjectives"
              value={inputs.learningObjectives}
              onChange={handleInputs}
              placeholder="Objetivos de aprendizaje (uno por línea)"
              rows={4}
            />
          </FormField>
        </FormSection>

        <FormSection>
          <SectionTitle>Configuración de Visibilidad</SectionTitle>

          <FormField>
            <Checkbox
              label="Curso Activo"
              checked={inputs.status}
              onChange={() => handleCheckbox('status')}
            />
          </FormField>

          <FormField>
            <Checkbox
              label="Curso Público"
              checked={inputs.isPublic}
              onChange={() => handleCheckbox('isPublic')}
            />
          </FormField>
        </FormSection>

        <ButtonContainer>
          <Button onClick={onCancel} disabled={loading}>
            Cancelar
          </Button>
          <Button type="submit" primary loading={loading} disabled={loading}>
            {course ? 'Actualizar Curso' : 'Crear Curso'}
          </Button>
        </ButtonContainer>
      </Form>
    </FormContainer>
  );
};

export default CourseForm;
